import React, { useState, useMemo, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import TEAMS from "../data/teams";

const C = {
  bg:"#0d1117", surf:"#161b22", card:"#1c2330",
  bdr:"#30363d", txt:"#e6edf3", muted:"#7d8590",
  blue:"#388bfd", grn:"#3fb950", red:"#f85149", ylw:"#e3b341",
};

// ── Lineup Card ────────────────────────────────────────────────────────────────

function LineupCard({ players, onChange, teamColor, allowRemove }) {
  function moveUp(i) {
    if (i === 0) return;
    const next = [...players];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  }
  function moveDown(i) {
    if (i === players.length - 1) return;
    const next = [...players];
    [next[i + 1], next[i]] = [next[i], next[i + 1]];
    onChange(next);
  }
  function removePlayer(i) {
    const next = players.filter((_, idx) => idx !== i);
    onChange(next);
  }
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {players.map((p, i) => (
        <div key={p} style={{ background:C.card, border:`1px solid ${C.bdr}`, borderRadius:10, padding:"10px 14px", display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ width:28, height:28, borderRadius:"50%", background:teamColor+"22", border:`1px solid ${teamColor}66`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <span style={{ color:teamColor, fontSize:12, fontWeight:700 }}>{i + 1}</span>
          </div>
          <p style={{ color:C.txt, fontSize:15, fontWeight:600, margin:0, flex:1 }}>{p}</p>
          <div style={{ display:"flex", gap:4, alignItems:"center" }}>
            {allowRemove && players.length > 3 && (
              <button onClick={() => removePlayer(i)}
                style={{ background:C.red+"22", color:C.red, border:`1px solid ${C.red}44`, borderRadius:6, width:28, height:28, fontSize:14, cursor:"pointer", fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center" }}>
                ✕
              </button>
            )}
            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
              <button onClick={() => moveUp(i)} disabled={i === 0}
                style={{ background:i===0?C.card:C.blue+"22", color:i===0?C.bdr:C.blue, border:`1px solid ${i===0?C.bdr:C.blue+"44"}`, borderRadius:6, width:32, height:28, fontSize:14, cursor:i===0?"default":"pointer", fontWeight:700 }}>▲</button>
              <button onClick={() => moveDown(i)} disabled={i === players.length - 1}
                style={{ background:i===players.length-1?C.card:C.blue+"22", color:i===players.length-1?C.bdr:C.blue, border:`1px solid ${i===players.length-1?C.bdr:C.blue+"44"}`, borderRadius:6, width:32, height:28, fontSize:14, cursor:i===players.length-1?"default":"pointer", fontWeight:700 }}>▼</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Diamond ────────────────────────────────────────────────────────────────────

function Diamond({ runners, sel, onSel, onMove, onScore, onOut, live, small }) {
  const sz  = small ? 38 : 48;
  const gap = small ? 2  : 4;
  const BASES = [
    { id:"second", label:"2nd", col:2, row:1 },
    { id:"third",  label:"3rd", col:1, row:2 },
    { id:"first",  label:"1st", col:3, row:2 },
    { id:"home",   label:"H",   col:2, row:3 },
  ];
  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gridTemplateRows:"repeat(3,auto)", maxWidth: small ? 150 : 220, margin:"0 auto 8px" }}>
        {BASES.map(b => {
          const runner = b.id !== "home" ? runners[b.id] : null;
          const isSel  = sel === b.id;
          const hasBg  = isSel ? C.blue+"33" : runner ? (runner.isGhost ? C.muted+"22" : C.grn+"33") : C.card;
          const border = isSel ? C.blue : runner ? (runner.isGhost ? C.muted : C.grn) : C.bdr;
          function click() {
            if (!live || b.id === "home") return;
            if (isSel) { onSel(null); return; }
            if (sel) { if (!runners[b.id]) onMove(sel, b.id); return; }
            if (runner) onSel(b.id);
          }
          return (
            <div key={b.id} style={{ gridColumn:b.col, gridRow:b.row, display:"flex", flexDirection:"column", alignItems:"center", gap, padding:`${gap}px 0` }}>
              <button onClick={click} style={{ width:sz, height:sz, transform:"rotate(45deg)", background:hasBg, border:`2px solid ${border}`, borderRadius:4, cursor: live && b.id !== "home" && (runner || sel) ? "pointer" : "default" }} />
              <p style={{ color:C.muted, fontSize: small ? 9 : 10, margin:0 }}>{b.label}</p>
              {runner && (
                <p style={{ color: runner.isGhost ? C.muted : C.grn, fontSize: small ? 9 : 11, fontWeight:700, margin:0, maxWidth: small ? 52 : 72, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", textAlign:"center" }}>
                  {runner.name}{runner.isGhost ? " (G)" : ""}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────

export default function GameScoring({ isAdmin }) {
  const emptyRunners = { first:null, second:null, third:null };

  const [phase, setPhase]   = useState("setup");
  const [homeId, setHomeId] = useState("");
  const [awayId, setAwayId] = useState("");
  const [awayLineup, setAwayLineup] = useState([]);
  const [homeLineup, setHomeLineup] = useState([]);
  const [inning, setInning] = useState(1);
  const [isTop,  setIsTop]  = useState(true);
  const [outs,   setOuts]   = useState(0);
  const [score,  setScore]  = useState({ home:0, away:0 });
  const [i1done, setI1done] = useState(false);
  const [balls,    setBalls]    = useState(0);
  const [strikes,  setStrikes]  = useState(0);
  const [ballPass, setBallPass] = useState(0);
  const [awaySubs, setAwaySubs] = useState([]);
  const [homeSubs, setHomeSubs] = useState([]);
  const [awayBat, setAwayBat] = useState(0);
  const [homeBat, setHomeBat] = useState(0);
  const [awayPit, setAwayPit] = useState(0);
  const [homePit, setHomePit] = useState(0);
  const [isPitcherSub, setIsPitcherSub] = useState(false);
  const [runners,     setRunners]     = useState(emptyRunners);
  const [prevRunners, setPrevRunners] = useState(emptyRunners);
  const [selBase,     setSelBase]     = useState(null);
  const [runPanel,    setRunPanel]    = useState(false);
  const [walkModal,   setWalkModal]   = useState(false);
  const [pickoffPanel, setPickoffPanel] = useState(false);
  const [stats,  setStats]  = useState({});
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [history, setHistory] = useState([]);
  const [activeGame,     setActiveGame]     = useState(null);
  const [checkingResume, setCheckingResume] = useState(true);

  // ── Check for active game on mount ────────────────────────────────────────
  useEffect(() => {
    async function checkActiveGame() {
      try {
        const snap = await getDoc(doc(db, "league", "activeGame"));
        if (snap.exists()) setActiveGame(snap.data());
      } catch (e) { console.error("Error checking active game:", e); }
      setCheckingResume(false);
    }
    checkActiveGame();
  }, []);

  // ── Snapshot for undo ─────────────────────────────────────────────────────
  function snapshot() {
    return {
      inning, isTop, outs, score, i1done,
      balls, strikes, ballPass,
      awaySubs: [...awaySubs], homeSubs: [...homeSubs],
      awayBat, homeBat, awayPit, homePit,
      isPitcherSub,
      runners: { ...runners }, prevRunners: { ...prevRunners },
      runPanel, walkModal, pickoffPanel, stats: { ...stats },
    };
  }

  function pushHistory() {
    const snap = snapshot();
    setHistory(prev => [snap, ...prev].slice(0, 5));
  }

  // ── Undo ──────────────────────────────────────────────────────────────────
  function handleUndo() {
    if (history.length === 0) return;
    const [prev, ...rest] = history;
    setHistory(rest);
    setInning(prev.inning);
    setIsTop(prev.isTop);
    setOuts(prev.outs);
    setScore(prev.score);
    setI1done(prev.i1done);
    setBalls(prev.balls);
    setStrikes(prev.strikes);
    setBallPass(prev.ballPass);
    setAwaySubs(prev.awaySubs);
    setHomeSubs(prev.homeSubs);
    setAwayBat(prev.awayBat);
    setHomeBat(prev.homeBat);
    setAwayPit(prev.awayPit);
    setHomePit(prev.homePit);
    setIsPitcherSub(prev.isPitcherSub);
    setRunners(prev.runners);
    setPrevRunners(prev.prevRunners);
    setRunPanel(prev.runPanel);
    setWalkModal(prev.walkModal);
    setPickoffPanel(prev.pickoffPanel || false);
    setStats(prev.stats);
    setSelBase(null);
    saveState({ ...prev });
  }

  // ── Save state to Firebase ────────────────────────────────────────────────
  function saveState(overrides = {}) {
    const gameState = {
      phase: "game",
      homeId, awayId, awayLineup, homeLineup,
      inning, isTop, outs, score, i1done,
      balls, strikes, ballPass,
      awaySubs, homeSubs,
      awayBat, homeBat, awayPit, homePit,
      isPitcherSub,
      runners, prevRunners, runPanel, walkModal, pickoffPanel, stats,
      savedAt: Date.now(),
      ...overrides,
    };
    setDoc(doc(db, "league", "activeGame"), gameState)
      .catch(e => console.error("Error saving game state:", e));
  }

  // ── Load saved game state ─────────────────────────────────────────────────
  function resumeGame(gs) {
    setPhase(gs.phase || "game");
    setHomeId(gs.homeId || "");
    setAwayId(gs.awayId || "");
    setAwayLineup(gs.awayLineup || []);
    setHomeLineup(gs.homeLineup || []);
    setInning(gs.inning || 1);
    setIsTop(gs.isTop !== undefined ? gs.isTop : true);
    setOuts(gs.outs || 0);
    setScore(gs.score || { home:0, away:0 });
    setI1done(gs.i1done || false);
    setBalls(gs.balls || 0);
    setStrikes(gs.strikes || 0);
    setBallPass(gs.ballPass || 0);
    setAwaySubs(gs.awaySubs || []);
    setHomeSubs(gs.homeSubs || []);
    setAwayBat(gs.awayBat || 0);
    setHomeBat(gs.homeBat || 0);
    setAwayPit(gs.awayPit || 0);
    setHomePit(gs.homePit || 0);
    setIsPitcherSub(gs.isPitcherSub || false);
    setRunners(gs.runners || emptyRunners);
    setPrevRunners(gs.prevRunners || emptyRunners);
    setRunPanel(gs.runPanel || false);
    setWalkModal(gs.walkModal || false);
    setPickoffPanel(gs.pickoffPanel || false);
    setStats(gs.stats || {});
    setActiveGame(null);
    setHistory([]);
  }

  function dismissResume() {
    setActiveGame(null);
    deleteDoc(doc(db, "league", "activeGame")).catch(console.error);
  }

  // ── Derived ────────────────────────────────────────────────────────────────
  const homeTeam = TEAMS.find(t => t.id === homeId);
  const awayTeam = TEAMS.find(t => t.id === awayId);
  const batTeam  = isTop ? awayTeam : homeTeam;
  const batId    = isTop ? awayId   : homeId;
  const defTeam  = isTop ? homeTeam : awayTeam;
  const defId    = isTop ? homeId   : awayId;

  const awayPlayers  = awayLineup.length > 0 ? awayLineup : (awayTeam?.players || []);
  const homePlayers  = homeLineup.length > 0 ? homeLineup : (homeTeam?.players || []);
  const batPlayers   = isTop ? awayPlayers : homePlayers;
  const defPlayers   = isTop ? homePlayers : awayPlayers;

  const awayRosterSize = awayPlayers.length || 3;
  const homeRosterSize = homePlayers.length || 3;
  const defRosterSize  = isTop ? homeRosterSize : awayRosterSize;

  const awayBatSlot = awayBat % awayRosterSize;
  const homeBatSlot = homeBat % homeRosterSize;
  const batSlot     = isTop ? awayBatSlot : homeBatSlot;
  const pitIdx      = isTop ? homePit : awayPit;

  const batter  = batPlayers[batSlot] || "";
  const pitcher = defPlayers[pitIdx % defRosterSize] || "";
  const isSub   = isTop ? (awaySubs[awayBatSlot] || false) : (homeSubs[homeBatSlot] || false);

  const runnersOnBase = ["first","second","third"].filter(b => runners[b]);

  function toggleSub() {
    if (isTop) {
      setAwaySubs(prev => {
        const n = [...prev];
        while (n.length < awayRosterSize) n.push(false);
        n[awayBatSlot] = !n[awayBatSlot];
        return n;
      });
    } else {
      setHomeSubs(prev => {
        const n = [...prev];
        while (n.length < homeRosterSize) n.push(false);
        n[homeBatSlot] = !n[homeBatSlot];
        return n;
      });
    }
  }

  const displayRunners = useMemo(() => {
    const dr = {
      first:  runners.first  ? { ...runners.first }  : null,
      second: runners.second ? { ...runners.second } : null,
      third:  runners.third  ? { ...runners.third }  : null,
    };
    if (!isSub && batter) {
      ["first","second","third"].forEach(base => {
        if (dr[base] && !dr[base].isGhost && dr[base].name === batter) {
          dr[base] = { ...dr[base], isGhost: true };
        }
      });
    }
    return dr;
  }, [runners, batter, isSub]);

  // ── Stat helpers ───────────────────────────────────────────────────────────

  function addStat(teamId, player, stat, val = 1) {
    if (isSub || !player) return;
    const k = `${teamId}|${player}`;
    setStats(prev => ({ ...prev, [k]: { ...(prev[k] || {}), [stat]: ((prev[k] || {})[stat] || 0) + val } }));
  }

  function addStatFor(teamId, player, isSubPlayer, stat, val = 1) {
    if (isSubPlayer || !player || !teamId) return;
    const k = `${teamId}|${player}`;
    setStats(prev => ({ ...prev, [k]: { ...(prev[k] || {}), [stat]: ((prev[k] || {})[stat] || 0) + val } }));
  }

  function addPit(stat, val = 1) {
    if (isPitcherSub || !pitcher) return;
    const k = `${defId}|${pitcher}`;
    setStats(prev => ({ ...prev, [k]: { ...(prev[k] || {}), [stat]: ((prev[k] || {})[stat] || 0) + val } }));
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  function resetCount() { setBalls(0); setStrikes(0); }

  function nextBat(currentIsTop) {
    const top = currentIsTop !== undefined ? currentIsTop : isTop;
    if (top) setAwayBat(i => i + 1);
    else setHomeBat(i => i + 1);
    resetCount();
    setBallPass(0);
  }

  function setPit(i) {
    pushHistory();
    if (isTop) setHomePit(i);
    else setAwayPit(i);
    setIsPitcherSub(false);
  }

  function snapshotRunners(r) {
    setPrevRunners({ first: r.first, second: r.second, third: r.third });
  }

  function makeRunner(name, isGhost, hitterName, hitterTeam, hitterIsSub) {
    return { name, isGhost, hitterName, hitterTeam, hitterIsSub };
  }

  // ── Mercy check ───────────────────────────────────────────────────────────
  function checkMercy(sc) {
    if (!i1done) return false;
    return Math.abs(sc.home - sc.away) >= 10;
  }

  function endGame(sc) {
    setScore(sc);
    setPhase("final");
    deleteDoc(doc(db, "league", "activeGame")).catch(console.error);
  }

  // ── Half-inning end ────────────────────────────────────────────────────────

  function endHalf(sc, curInning, curIsTop, curI1done) {
    const newRunners = emptyRunners;
    snapshotRunners(newRunners);
    setRunners(newRunners);
    setSelBase(null); setRunPanel(false); setPickoffPanel(false);
    setOuts(0); resetCount(); setBallPass(0);
    setIsPitcherSub(false);

    let nextInning = curInning;
    let nextIsTop  = !curIsTop;
    let nextI1done = curI1done;
    let shouldEnd  = false;

    if (!curIsTop) {
      const completingI1 = curInning === 1;
      if (completingI1) { nextI1done = true; setI1done(true); }
      const mercyOk = completingI1 || curI1done;
      if (mercyOk && Math.abs(sc.home - sc.away) >= 10) { shouldEnd = true; }
      else if (curInning >= 3) { shouldEnd = true; }
      else { nextInning = curInning + 1; setInning(nextInning); }
    } else {
      if (curI1done && Math.abs(sc.home - sc.away) >= 10) { shouldEnd = true; }
    }

    if (shouldEnd) { endGame(sc); return; }

    setScore(sc);
    setIsTop(nextIsTop);
    saveState({
      inning: nextInning, isTop: nextIsTop, outs: 0,
      score: sc, i1done: nextI1done,
      balls: 0, strikes: 0, ballPass: 0,
      runners: newRunners, prevRunners: newRunners,
      runPanel: false, walkModal: false, pickoffPanel: false,
      isPitcherSub: false,
    });
  }

  function doOut(sc, curInning, curIsTop, curI1done, curOuts) {
    addPit("outs");
    nextBat(curIsTop);
    const no = curOuts + 1;
    if (no >= 2) { setOuts(0); endHalf(sc, curInning, curIsTop, curI1done); }
    else { setOuts(no); saveState({ outs: no, score: sc, balls: 0, strikes: 0, ballPass: 0 }); }
  }

  // ── Pickoff ────────────────────────────────────────────────────────────────

  function handlePickoff(base) {
    pushHistory();
    const r = { ...runners };
    r[base] = null;
    setRunners(r);
    setPickoffPanel(false);
    setSelBase(null);
    addPit("outs");
    const no = outs + 1;
    if (no >= 2) { setOuts(0); endHalf(score, inning, isTop, i1done); }
    else { setOuts(no); saveState({ runners: r, outs: no, pickoffPanel: false }); }
  }

  // ── Ball ───────────────────────────────────────────────────────────────────

  function handleBall() {
    pushHistory();
    const nb = balls + 1;
    if (nb >= 4) {
      if (ballPass === 0)      { resetCount(); setBallPass(1); saveState({ balls: 0, strikes: 0, ballPass: 1 }); }
      else if (ballPass === 1) { resetCount(); setWalkModal(true); saveState({ balls: 0, strikes: 0, walkModal: true }); }
      else                     { doWalk(); }
    } else { setBalls(nb); saveState({ balls: nb }); }
  }

  // ── Strike ─────────────────────────────────────────────────────────────────

  function handleStrike() {
    pushHistory();
    const ns = strikes + 1;
    if (ns >= 3) {
      addStat(batId, batter, "AB");
      addPit("SO");
      resetCount();
      doOut(score, inning, isTop, i1done, outs);
    } else { setStrikes(ns); saveState({ strikes: ns }); }
  }

  // ── Out in play ────────────────────────────────────────────────────────────

  function handleOut() {
    pushHistory();
    addStat(batId, batter, "AB");
    doOut(score, inning, isTop, i1done, outs);
  }

  // ── Walk ───────────────────────────────────────────────────────────────────

  function doWalk() {
    pushHistory();
    setWalkModal(false);
    const br   = makeRunner(isSub ? "Sub" : batter, false, batter, batId, isSub);
    const r    = { ...runners };
    const side = isTop ? "away" : "home";
    let newScore = { ...score };

    addStat(batId, batter, "BB");

    if (r.first && r.second && r.third) {
      addStatFor(batId, batter, isSub, "RBI", 1);
      addPit("ER");
      newScore = { ...newScore, [side]: newScore[side] + 1 };
      r.third = r.second; r.second = r.first; r.first = br;
      if (checkMercy(newScore)) { snapshotRunners(runners); setRunners(r); endGame(newScore); return; }
    } else if (r.first && r.second) { r.third = r.second; r.second = r.first; r.first = br; }
    else if (r.first)               { r.second = r.first; r.first = br; }
    else                             { r.first = br; }

    const newAwayBat = isTop ? awayBat + 1 : awayBat;
    const newHomeBat = isTop ? homeBat : homeBat + 1;

    snapshotRunners(runners);
    setRunners(r); setScore(newScore);
    nextBat(isTop);

    saveState({
      runners: r, prevRunners: runners, score: newScore,
      walkModal: false, balls: 0, strikes: 0, ballPass: 0,
      awayBat: newAwayBat, homeBat: newHomeBat,
    });
  }

  // ── Hit ────────────────────────────────────────────────────────────────────

  function handleHit(type) {
    pushHistory();
    addStat(batId, batter, "AB");
    addStat(batId, batter, "H");
    addStat(batId, batter, type);
    const br = makeRunner(isSub ? "Sub" : batter, false, batter, batId, isSub);

    if (type === "HR") {
      let runs = 1;
      Object.values(runners).forEach(r => { if (r) runs++; });
      addStatFor(batId, batter, isSub, "RBI", runs);
      addPit("ER", runs);
      const side = isTop ? "away" : "home";
      const ns = { ...score, [side]: score[side] + runs };
      snapshotRunners(runners);
      setRunners(emptyRunners);
      const newAwayBat = isTop ? awayBat + 1 : awayBat;
      const newHomeBat = isTop ? homeBat : homeBat + 1;
      nextBat(isTop); setScore(ns);
      if (checkMercy(ns)) { endGame(ns); return; }
      saveState({
        runners: emptyRunners, prevRunners: runners, score: ns,
        balls: 0, strikes: 0, ballPass: 0,
        awayBat: newAwayBat, homeBat: newHomeBat, runPanel: false,
      });
      return;
    }

    const basesHit  = type === "1B" ? 1 : type === "2B" ? 2 : 3;
    const baseOrder = ["first", "second", "third"];
    const nr        = { first: null, second: null, third: null };
    let   runs      = 0;
    const side      = isTop ? "away" : "home";

    baseOrder.forEach((base, idx) => {
      const runner = runners[base];
      if (!runner) return;
      const newIdx = idx + basesHit;
      if (newIdx >= 3) {
        runs++;
        addStatFor(runner.hitterTeam, runner.hitterName, runner.hitterIsSub, "RBI", 1);
        addPit("ER");
      } else {
        nr[baseOrder[newIdx]] = runner;
      }
    });

    nr[baseOrder[basesHit - 1]] = br;
    const newScore = { ...score, [side]: score[side] + runs };
    const newAwayBat = isTop ? awayBat + 1 : awayBat;
    const newHomeBat = isTop ? homeBat : homeBat + 1;
    const prevR = { ...runners };

    snapshotRunners(runners);
    setRunners(nr); setScore(newScore);
    nextBat(isTop);

    if (runs > 0 && checkMercy(newScore)) { endGame(newScore); return; }

    setRunPanel(true);
    saveState({
      runners: nr, prevRunners: prevR, score: newScore,
      balls: 0, strikes: 0, ballPass: 0,
      awayBat: newAwayBat, homeBat: newHomeBat, runPanel: true,
    });
  }

  // ── Runner panel ───────────────────────────────────────────────────────────

  function doneRunners() {
    snapshotRunners(runners);
    setRunPanel(false); setSelBase(null);
    saveState({ prevRunners: runners, runPanel: false });
  }

  function moveRunner(from, to) {
    pushHistory();
    const r = { ...runners };
    r[to] = r[from]; r[from] = null;
    setRunners(r); setSelBase(null);
    saveState({ runners: r });
  }

  function runnerScores(from) {
    pushHistory();
    const runner = runners[from];
    const r      = { ...runners };
    r[from]      = null;
    const side   = isTop ? "away" : "home";
    const ns     = { ...score, [side]: score[side] + 1 };
    if (runner) addStatFor(runner.hitterTeam, runner.hitterName, runner.hitterIsSub, "RBI", 1);
    addPit("ER");
    setRunners(r); setScore(ns); setSelBase(null);
    saveState({ runners: r, score: ns });
    if (checkMercy(ns)) { setRunPanel(false); endGame(ns); }
  }

  function tagOut(base) {
    pushHistory();
    const r = { ...runners };
    r[base] = null;
    setRunners(r); setSelBase(null);
    addPit("outs");
    const no = outs + 1;
    if (no >= 2) { setRunPanel(false); setOuts(0); endHalf(score, inning, isTop, i1done); }
    else { setOuts(no); saveState({ runners: r, outs: no }); }
  }

  // ── Save to Firebase ───────────────────────────────────────────────────────

  async function saveGame() {
    setSaving(true);
    try {
      const winId  = score.home > score.away ? homeId : score.away > score.home ? awayId : null;
      const loseId = score.home > score.away ? awayId : score.away > score.home ? homeId : null;
      const sRef  = doc(db, "league", "standings");
      const sSnap = await getDoc(sRef);
      const stnd  = sSnap.exists() ? sSnap.data() : {};
      TEAMS.forEach(t => { if (!stnd[t.id]) stnd[t.id] = { W:0, L:0 }; });
      if (winId) { stnd[winId].W += 1; stnd[loseId].L += 1; }
      await setDoc(sRef, stnd);
      const pRef  = doc(db, "league", "playerStats");
      const pSnap = await getDoc(pRef);
      const all   = pSnap.exists() ? pSnap.data() : {};
      Object.entries(stats).forEach(([k, v]) => {
        if (!all[k]) all[k] = {};
        Object.entries(v).forEach(([s, n]) => { all[k][s] = (all[k][s] || 0) + n; });
      });
      await setDoc(pRef, all);
      await deleteDoc(doc(db, "league", "activeGame"));
      setSaved(true);
    } catch (e) {
      console.error(e);
      alert("Error saving. Please try again.");
    }
    setSaving(false);
  }

  // ── Reset all ──────────────────────────────────────────────────────────────

  function resetAll() {
    deleteDoc(doc(db, "league", "activeGame")).catch(console.error);
    setPhase("setup"); setHomeId(""); setAwayId("");
    setAwayLineup([]); setHomeLineup([]);
    setInning(1); setIsTop(true); setOuts(0);
    setScore({ home:0, away:0 }); setI1done(false);
    setAwayBat(0); setHomeBat(0); setAwayPit(0); setHomePit(0);
    setIsPitcherSub(false);
    setAwaySubs([]); setHomeSubs([]);
    setRunners(emptyRunners); setPrevRunners(emptyRunners);
    setStats({}); setSaved(false); resetCount(); setBallPass(0);
    setRunPanel(false); setSelBase(null); setWalkModal(false);
    setPickoffPanel(false);
    setActiveGame(null); setHistory([]);
  }

  // ── Setup screen ───────────────────────────────────────────────────────────

  if (phase === "setup") return (
    <div style={{ padding:"20px 14px" }}>
      {isAdmin && !checkingResume && activeGame && (
        <div style={{ background:"#1a2a1a", border:`1px solid ${C.grn}66`, borderRadius:12, padding:"14px 16px", marginBottom:20 }}>
          <p style={{ color:C.grn, fontSize:13, fontWeight:700, margin:"0 0 4px" }}>⚾ Game In Progress</p>
          <p style={{ color:C.muted, fontSize:12, margin:"0 0 12px" }}>
            {TEAMS.find(t => t.id === activeGame.awayId)?.abbr || "?"} vs {TEAMS.find(t => t.id === activeGame.homeId)?.abbr || "?"} — {activeGame.isTop ? "▲" : "▼"}{activeGame.inning}, {activeGame.score?.away ?? 0}–{activeGame.score?.home ?? 0}
          </p>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            <button onClick={() => resumeGame(activeGame)}
              style={{ background:C.grn, color:"#fff", border:"none", borderRadius:8, padding:11, fontSize:14, fontWeight:700, cursor:"pointer" }}>
              Resume Game
            </button>
            <button onClick={dismissResume}
              style={{ background:"transparent", color:C.muted, border:`1px solid ${C.bdr}`, borderRadius:8, padding:11, fontSize:13, cursor:"pointer" }}>
              Discard
            </button>
          </div>
        </div>
      )}

      <p style={{ color:C.muted, fontSize:11, fontWeight:700, letterSpacing:"1px", margin:"0 0 18px" }}>NEW GAME</p>

      <label style={{ color:C.muted, fontSize:13, display:"block", marginBottom:6 }}>Away Team</label>
      <select value={awayId} onChange={e => {
          const id = e.target.value; setAwayId(id);
          const team = TEAMS.find(t => t.id === id);
          if (team) { setAwayLineup([...team.players]); setAwaySubs(team.players.map(() => false)); }
        }}
        style={{ background:C.card, border:`1px solid ${C.bdr}`, borderRadius:8, padding:"12px 14px", color:C.txt, fontSize:15, width:"100%", marginBottom:12 }}>
        <option value="">Select team...</option>
        {TEAMS.filter(t => t.id !== homeId).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>

      {awayId && (
        <div style={{ marginBottom:18 }}>
          <p style={{ color:C.muted, fontSize:11, fontWeight:700, letterSpacing:"0.8px", margin:"0 0 8px" }}>
            AWAY BATTING ORDER {awayLineup.length > 3 && <span style={{ color:C.red, fontWeight:400 }}>— tap ✕ to remove a player for this game</span>}
          </p>
          <LineupCard
            players={awayLineup.length > 0 ? awayLineup : (TEAMS.find(t => t.id === awayId)?.players || [])}
            onChange={p => { setAwayLineup(p); setAwaySubs(p.map(() => false)); }}
            teamColor={TEAMS.find(t => t.id === awayId)?.color || C.blue}
            allowRemove={true}
          />
        </div>
      )}

      <p style={{ color:C.muted, fontSize:14, textAlign:"center", margin:"0 0 12px" }}>vs.</p>

      <label style={{ color:C.muted, fontSize:13, display:"block", marginBottom:6 }}>Home Team</label>
      <select value={homeId} onChange={e => {
          const id = e.target.value; setHomeId(id);
          const team = TEAMS.find(t => t.id === id);
          if (team) { setHomeLineup([...team.players]); setHomeSubs(team.players.map(() => false)); }
        }}
        style={{ background:C.card, border:`1px solid ${C.bdr}`, borderRadius:8, padding:"12px 14px", color:C.txt, fontSize:15, width:"100%", marginBottom:12 }}>
        <option value="">Select team...</option>
        {TEAMS.filter(t => t.id !== awayId).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>

      {homeId && (
        <div style={{ marginBottom:20 }}>
          <p style={{ color:C.muted, fontSize:11, fontWeight:700, letterSpacing:"0.8px", margin:"0 0 8px" }}>
            HOME BATTING ORDER {homeLineup.length > 3 && <span style={{ color:C.red, fontWeight:400 }}>— tap ✕ to remove a player for this game</span>}
          </p>
          <LineupCard
            players={homeLineup.length > 0 ? homeLineup : (TEAMS.find(t => t.id === homeId)?.players || [])}
            onChange={p => { setHomeLineup(p); setHomeSubs(p.map(() => false)); }}
            teamColor={TEAMS.find(t => t.id === homeId)?.color || C.blue}
            allowRemove={true}
          />
        </div>
      )}

      <button onClick={() => { if (homeId && awayId) setPhase("game"); }}
        style={{ background:homeId&&awayId?C.blue:C.bdr, color:"#fff", border:"none", borderRadius:8, padding:14, fontSize:15, fontWeight:700, cursor:homeId&&awayId?"pointer":"default", width:"100%" }}>
        Start Game
      </button>
    </div>
  );

  // ── Final screen ───────────────────────────────────────────────────────────

  if (phase === "final") {
    const homeWin = score.home > score.away;
    const awayWin = score.away > score.home;
    const winner  = homeWin ? homeTeam : awayWin ? awayTeam : null;
    return (
      <div style={{ padding:"30px 20px", textAlign:"center" }}>
        <div style={{ fontSize:52, marginBottom:12 }}>🏆</div>
        <p style={{ color:C.muted, fontSize:11, fontWeight:700, letterSpacing:"1px", margin:"0 0 8px" }}>FINAL</p>
        <h2 style={{ color:C.txt, fontSize:22, margin:"0 0 6px" }}>{winner ? `${winner.name} Win!` : "Tie Game!"}</h2>
        <p style={{ color:C.muted, fontSize:18, margin:"0 0 28px" }}>{awayTeam?.abbr} {score.away} — {score.home} {homeTeam?.abbr}</p>
        {isAdmin && !saved && (
          <button onClick={saveGame} disabled={saving}
            style={{ background:C.blue, color:"#fff", border:"none", borderRadius:8, padding:14, fontSize:15, fontWeight:700, cursor:saving?"default":"pointer", width:"100%", marginBottom:10 }}>
            {saving ? "Saving..." : "Save to League Records"}
          </button>
        )}
        {saved && <p style={{ color:C.grn, fontWeight:700, marginBottom:12 }}>Saved to league records!</p>}
        <button onClick={resetAll}
          style={{ background:"transparent", color:C.muted, border:`1px solid ${C.bdr}`, borderRadius:8, padding:13, fontSize:14, cursor:"pointer", width:"100%" }}>
          New Game
        </button>
      </div>
    );
  }

  // ── Game screen ────────────────────────────────────────────────────────────

  const canUndo = history.length > 0;

  return (
    <div style={{ padding:14 }}>

      {/* Scoreboard */}
      <div style={{ background:C.surf, borderRadius:12, border:`1px solid ${C.bdr}`, padding:"14px 16px", marginBottom:12 }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr auto 1fr", gap:8, alignItems:"center", marginBottom:10 }}>
          <div style={{ textAlign:"center" }}>
            <p style={{ color:C.muted, fontSize:11, margin:"0 0 2px" }}>{awayTeam?.abbr} AWAY</p>
            <p style={{ color:C.txt, fontSize:36, fontWeight:800, margin:0, lineHeight:1 }}>{score.away}</p>
          </div>
          <div style={{ textAlign:"center", padding:"0 8px" }}>
            <p style={{ color:C.muted, fontSize:18, fontWeight:700, margin:0 }}>{isTop ? "▲" : "▼"}{inning}</p>
            <p style={{ color:C.muted, fontSize:12, margin:0 }}>{outs} out{outs !== 1 ? "s" : ""}</p>
          </div>
          <div style={{ textAlign:"center" }}>
            <p style={{ color:C.muted, fontSize:11, margin:"0 0 2px" }}>{homeTeam?.abbr} HOME</p>
            <p style={{ color:C.txt, fontSize:36, fontWeight:800, margin:0, lineHeight:1 }}>{score.home}</p>
          </div>
        </div>
        <div style={{ borderTop:`1px solid ${C.bdr}`, paddingTop:8, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ display:"flex", gap:20 }}>
            <span style={{ color:C.muted, fontSize:13 }}>
              Balls: <b style={{ color:C.grn }}>{balls}</b>
              {ballPass > 0 && <span style={{ color:C.ylw, fontSize:11 }}> (pass {ballPass + 1})</span>}
            </span>
            <span style={{ color:C.muted, fontSize:13 }}>Strikes: <b style={{ color:C.red }}>{strikes}</b></span>
          </div>
          <button onClick={handleUndo} disabled={!canUndo}
            style={{ background:canUndo?C.ylw+"22":C.card, color:canUndo?C.ylw:C.bdr, border:`1px solid ${canUndo?C.ylw+"66":C.bdr}`, borderRadius:8, padding:"5px 12px", fontSize:12, fontWeight:700, cursor:canUndo?"pointer":"default" }}>
            ↩ Undo {history.length > 0 && `(${history.length})`}
          </button>
        </div>
      </div>

      {/* Batter & Pitcher */}
      <div style={{ background:C.surf, borderRadius:12, border:`1px solid ${C.bdr}`, padding:"12px 14px", marginBottom:12 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
          <div>
            <p style={{ color:C.muted, fontSize:11, margin:"0 0 2px" }}>BATTING · {batTeam?.abbr}</p>
            <p style={{ color:C.txt, fontSize:16, fontWeight:700, margin:0 }}>{batter}{isSub ? " (SUB)" : ""}</p>
          </div>
          <button onClick={toggleSub}
            style={{ background:isSub?"#2d2a0e":C.card, color:isSub?C.ylw:C.muted, border:`1px solid ${isSub?C.ylw+"88":C.bdr}`, borderRadius:20, padding:"5px 12px", fontSize:12, fontWeight:700, cursor:"pointer" }}>
            {isSub ? "SUB ON" : "SUB"}
          </button>
        </div>

        <div style={{ borderTop:`1px solid ${C.bdr}`, paddingTop:10 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
            <p style={{ color:C.muted, fontSize:11, margin:0 }}>
              PITCHING · {defTeam?.abbr}
              {isPitcherSub && <span style={{ color:C.ylw, marginLeft:6 }}>(SUB)</span>}
            </p>
            <button onClick={() => { pushHistory(); setIsPitcherSub(s => !s); }}
              style={{ background:isPitcherSub?"#2d2a0e":C.card, color:isPitcherSub?C.ylw:C.muted, border:`1px solid ${isPitcherSub?C.ylw+"88":C.bdr}`, borderRadius:20, padding:"3px 10px", fontSize:11, fontWeight:700, cursor:"pointer" }}>
              {isPitcherSub ? "SUB ON" : "SUB"}
            </button>
          </div>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {defPlayers.map((p, i) => (
              <button key={p} onClick={() => setPit(i)}
                style={{ flex:1, minWidth:60, background:pitIdx%defRosterSize===i?C.blue:C.card, color:pitIdx%defRosterSize===i?"#fff":C.muted, border:`1px solid ${pitIdx%defRosterSize===i?C.blue:C.bdr}`, borderRadius:8, padding:"7px 4px", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Dual Diamond */}
      <div style={{ background:C.surf, borderRadius:12, border:`1px solid ${C.bdr}`, padding:"12px 14px", marginBottom:12 }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1px 1fr", gap:8, alignItems:"start" }}>
          <div>
            <p style={{ color:C.muted, fontSize:10, fontWeight:700, letterSpacing:"0.8px", margin:"0 0 8px", textAlign:"center" }}>AFTER LAST AT-BAT</p>
            <Diamond runners={prevRunners} sel={null} onSel={() => {}} onMove={() => {}} onScore={() => {}} onOut={() => {}} live={false} small={true} />
          </div>
          <div style={{ background:C.bdr, height:"100%", minHeight:140 }} />
          <div>
            <p style={{ color: runPanel ? C.blue : C.muted, fontSize:10, fontWeight:700, letterSpacing:"0.8px", margin:"0 0 8px", textAlign:"center" }}>
              {runPanel ? "MOVE RUNNERS ▼" : "CURRENT"}
            </p>
            <Diamond runners={displayRunners} sel={selBase} onSel={setSelBase} onMove={moveRunner} onScore={runnerScores} onOut={tagOut} live={runPanel} small={true} />
          </div>
        </div>

        {runPanel && (
          <>
            {selBase && runners[selBase] && (
              <div style={{ background:C.card, borderRadius:8, border:`1px solid ${C.bdr}`, padding:"10px 12px", marginTop:10 }}>
                <p style={{ color:C.muted, fontSize:12, margin:"0 0 8px" }}>
                  Move <b style={{ color:C.txt }}>{runners[selBase].name}</b> to:
                </p>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {[["first","1st"],["second","2nd"],["third","3rd"]]
                    .filter(([id]) => id !== selBase && !runners[id])
                    .map(([id, lbl]) => (
                      <button key={id} onClick={() => moveRunner(selBase, id)}
                        style={{ background:C.blue+"22", color:C.blue, border:`1px solid ${C.blue}44`, borderRadius:6, padding:"5px 10px", fontSize:12, cursor:"pointer" }}>
                        {lbl}
                      </button>
                    ))}
                  <button onClick={() => runnerScores(selBase)}
                    style={{ background:C.grn+"22", color:C.grn, border:`1px solid ${C.grn}44`, borderRadius:6, padding:"5px 10px", fontSize:12, cursor:"pointer" }}>
                    Scored!
                  </button>
                  <button onClick={() => tagOut(selBase)}
                    style={{ background:C.red+"22", color:C.red, border:`1px solid ${C.red}44`, borderRadius:6, padding:"5px 10px", fontSize:12, cursor:"pointer" }}>
                    Tagged Out
                  </button>
                </div>
              </div>
            )}
            <button onClick={doneRunners}
              style={{ background:C.grn, color:"#fff", border:"none", borderRadius:8, padding:11, fontSize:14, fontWeight:700, cursor:"pointer", width:"100%", marginTop:10 }}>
              Done — Next Batter
            </button>
          </>
        )}
      </div>

      {/* Pickoff panel */}
      {pickoffPanel && !runPanel && (
        <div style={{ background:C.card, borderRadius:12, border:`1px solid ${C.red}66`, padding:16, marginBottom:8 }}>
          <p style={{ color:C.red, fontWeight:700, fontSize:14, margin:"0 0 10px", textAlign:"center" }}>
            🎯 Pickoff — select runner
          </p>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {runnersOnBase.map(base => (
              <button key={base} onClick={() => handlePickoff(base)}
                style={{ flex:1, background:C.red+"22", color:C.red, border:`1px solid ${C.red}44`, borderRadius:8, padding:"10px 8px", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                {runners[base]?.name} ({base === "first" ? "1st" : base === "second" ? "2nd" : "3rd"})
              </button>
            ))}
          </div>
          <button onClick={() => setPickoffPanel(false)}
            style={{ background:"transparent", color:C.muted, border:`1px solid ${C.bdr}`, borderRadius:8, padding:"8px", fontSize:13, cursor:"pointer", width:"100%", marginTop:10 }}>
            Cancel
          </button>
        </div>
      )}

      {/* Action buttons or walk modal */}
      {!runPanel && (
        walkModal ? (
          <div style={{ background:C.card, borderRadius:12, border:`1px solid ${C.ylw}88`, padding:16 }}>
            <p style={{ color:C.ylw, fontWeight:700, fontSize:15, margin:"0 0 4px", textAlign:"center" }}>Walk Option</p>
            <p style={{ color:C.muted, fontSize:13, margin:"0 0 14px", textAlign:"center" }}>{batter} has seen 8 balls. Walk or keep hitting?</p>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <button onClick={doWalk} style={{ background:C.grn, color:"#fff", border:"none", borderRadius:8, padding:13, fontWeight:700, cursor:"pointer" }}>Take Walk</button>
              <button onClick={() => { setWalkModal(false); setBallPass(2); saveState({ walkModal: false, ballPass: 2 }); }}
                style={{ background:C.card, color:C.txt, border:`1px solid ${C.bdr}`, borderRadius:8, padding:13, fontWeight:700, cursor:"pointer" }}>Keep Hitting</button>
            </div>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:8 }}>
              {[["Ball", handleBall, C.grn], ["Strike", handleStrike, C.red], ["Out", handleOut, C.ylw]].map(([lbl, fn, clr]) => (
                <button key={lbl} onClick={fn}
                  style={{ background:clr+"22", color:clr, border:`1px solid ${clr}44`, borderRadius:8, padding:"14px 4px", fontSize:17, fontWeight:700, cursor:"pointer" }}>
                  {lbl}
                </button>
              ))}
              <button
                onClick={() => { if (runnersOnBase.length > 0) setPickoffPanel(p => !p); }}
                disabled={runnersOnBase.length === 0}
                style={{ background: runnersOnBase.length > 0 ? C.red+"22" : C.card, color: runnersOnBase.length > 0 ? C.red : C.bdr, border:`1px solid ${runnersOnBase.length > 0 ? C.red+"44" : C.bdr}`, borderRadius:8, padding:"14px 4px", fontSize:13, fontWeight:700, cursor: runnersOnBase.length > 0 ? "pointer" : "default" }}>
                Pickoff
              </button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:8 }}>
              {[["1B", C.blue], ["2B", C.blue], ["3B", C.blue], ["HR", "#f97316"]].map(([lbl, clr]) => (
                <button key={lbl} onClick={() => handleHit(lbl)}
                  style={{ background:clr+"22", color:clr, border:`1px solid ${clr}44`, borderRadius:8, padding:"12px 4px", fontSize:15, fontWeight:700, cursor:"pointer" }}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
        )
      )}
    </div>
  );
}